import CalendarToday from '@mui/icons-material/CalendarToday'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { CampaignSnsRequirementFragment } from '~/generated/graphql'
import { CampaignSnsRequirements } from '~/lib/types/Campaign'
import { DateTime } from 'luxon'
import { SnsIcon } from '~/components/common/SnsIcon'
import { numberHelper } from '~/lib/numberHelper'
import { MoneyIcon, TrainIcon } from '~/components/icons'
import React from 'react'
import { MultilineTypography } from '~/components/common/MultilineTypography'

type Props = {
  campaignName: string
  campaignNameStyle: 'ellipsis' | 'lineBreak'
  campaignSnsRequirements?: CampaignSnsRequirements[] | readonly CampaignSnsRequirementFragment[] | null
  gross?: number | null
  nearestStation?: string | null
  offerStarted?: string | null
  offerEnded?: string | null
}

const getOfferPeriodText = (offerStarted?: string | null, offerEnded?: string | null) => {
  if (!offerStarted || !offerEnded) return null

  return DateTime.fromISO(offerStarted).toFormat('yy/MM/dd') + '~' + DateTime.fromISO(offerEnded).toFormat('yy/MM/dd')
}

const useCampaignInfo = ({
  campaignName,
  gross,
  nearestStation,
  offerStarted,
  offerEnded,
  campaignSnsRequirements,
}: Props) => {
  return {
    campaignName,
    campaignSnsRequirements,
    grossString: gross ? numberHelper.toCurrencyString(gross) : null,
    nearestStation,
    offerPeriodText: getOfferPeriodText(offerStarted, offerEnded),
  }
}

export const CampaignInfo: React.FC<Props> = (props) => {
  const { campaignName, grossString, nearestStation, offerPeriodText, campaignSnsRequirements } = useCampaignInfo(props)

  return (
    <Stack spacing={1} direction="column">
      {props.campaignNameStyle === 'ellipsis' ? (
        <MultilineTypography variant="body2" color="text.primary" sx={{ fontWeight: 'bold' }} maxLines={1}>
          {campaignName}
        </MultilineTypography>
      ) : (
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'bold' }}>
          {campaignName}
        </Typography>
      )}

      {offerPeriodText && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarToday sx={{ fontSize: 16, color: 'text.primary' }} />
          <Typography variant="labelMEnNormal" color="text.primary">
            {offerPeriodText}
          </Typography>
        </Box>
      )}

      {campaignSnsRequirements && campaignSnsRequirements.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {campaignSnsRequirements.map((e, i) => (
            <SnsIcon key={i} snsType={e.snsType} size={16} color="text.primary" />
          ))}
        </Box>
      )}

      {grossString && (
        <Tooltip title="投稿報酬単価" placement="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MoneyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="bodyM" color="info.main" sx={{ fontWeight: 500 }}>
              {grossString}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {nearestStation && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrainIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="bodyM" color="text.primary">
            {nearestStation}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
